import React, {FC, useMemo, useRef, useState} from 'react'
import {Overlay, Popover} from 'react-bootstrap'

interface SyncMessageBadgeProps {
  syncMessage?: string
  showPopover?: boolean
  badgeContent: React.ReactNode
  badgeStyle?: React.CSSProperties
  popoverId?: string
  emptyMessage?: string
}

const SyncMessageBadge: FC<SyncMessageBadgeProps> = ({
  syncMessage,
  showPopover = true,
  badgeContent,
  badgeStyle,
  popoverId = 'status-message-popover',
  emptyMessage = 'No sync message available',
}) => {
  const [expanded, setExpanded] = useState(false)
  const [copied, setCopied] = useState(false)
  const [showMessage, setShowMessage] = useState(false)
  const [messagePlacement, setMessagePlacement] = useState<'right' | 'left'>('right')
  const targetRef = useRef<HTMLSpanElement | null>(null)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const message = syncMessage ? String(syncMessage) : emptyMessage
  const previewLimit = 260
  const normalizedMessage = useMemo(() => message.replace(/\s+/g, ' ').trim(), [message])
  const isLongMessage = normalizedMessage.length > previewLimit
  const displayMessage =
    isLongMessage && !expanded
      ? `${normalizedMessage.slice(0, previewLimit).trim()}...`
      : message

  const handleCopy = async (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault()
    ev.stopPropagation()

    try {
      await navigator.clipboard.writeText(message)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch (error) {
      console.error('Unable to copy sync message', error)
    }
  }

  const clearHideTimer = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current)
      hideTimerRef.current = null
    }
  }

  const openMessage = () => {
    if (!showPopover) return

    clearHideTimer()
    const targetRect = targetRef.current?.getBoundingClientRect()

    if (targetRect) {
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth
      const popoverWidth = Math.min(420, viewportWidth - 32)
      const minGap = 16
      const rightSpace = viewportWidth - targetRect.right
      const leftSpace = targetRect.left
      const hasRightSpace = rightSpace >= popoverWidth + minGap
      const hasLeftSpace = leftSpace >= popoverWidth + minGap

      setMessagePlacement(!hasRightSpace && (hasLeftSpace || leftSpace > rightSpace) ? 'left' : 'right')
    }

    setShowMessage(true)
  }

  const closeMessage = () => {
    clearHideTimer()
    hideTimerRef.current = setTimeout(() => {
      setShowMessage(false)
      setExpanded(false)
    }, 180)
  }

  const renderPopover = () => (
    <Popover
      id={popoverId}
      style={{
        width: 'min(420px, calc(100vw - 32px))',
        maxWidth: 'calc(100vw - 32px)',
        border: '1px solid #D0D5DD',
        borderRadius: '8px',
        boxShadow: '0 12px 28px rgba(16, 24, 40, 0.16)',
      }}
      onMouseEnter={openMessage}
      onMouseLeave={closeMessage}
    >
      <Popover.Body style={{padding: 0}}>
        <div
          className='d-flex align-items-center justify-content-between gap-3'
          style={{
            padding: '10px 12px 8px',
            borderBottom: '1px solid #EAECF0',
            background: '#F9FAFB',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
          }}
        >
          <div className='fw-semibold' style={{fontSize: '12px', color: '#101828'}}>
            Sync message
          </div>
          <button
            type='button'
            className='btn btn-sm p-0'
            style={{
              color: copied ? '#079455' : '#0D6EFD',
              fontSize: '12px',
              fontWeight: 600,
              lineHeight: 1,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
            }}
            onClick={handleCopy}
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <div style={{padding: '10px 12px 12px', background: '#FFFFFF'}}>
          <div
            style={{
              color: '#344054',
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
              fontSize: '11px',
              lineHeight: 1.55,
              maxHeight: expanded ? '260px' : '74px',
              overflowY: expanded ? 'auto' : 'hidden',
              paddingRight: expanded ? '6px' : 0,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {displayMessage}
          </div>
          {isLongMessage ? (
            <div
              className='d-flex align-items-center justify-content-end'
              style={{marginTop: '10px'}}
            >
              <button
                type='button'
                className='btn btn-sm p-0'
                style={{
                  color: '#0D6EFD',
                  fontSize: '12px',
                  fontWeight: 600,
                  lineHeight: 1,
                  textDecoration: 'none',
                }}
                onClick={(ev) => {
                  ev.preventDefault()
                  ev.stopPropagation()
                  setExpanded((value) => !value)
                }}
              >
                {expanded ? 'Show Less' : 'Show More'}
              </button>
            </div>
          ) : null}
        </div>
      </Popover.Body>
    </Popover>
  )

  return (
    <>
      <span
        className='badge rounded-pill'
        ref={targetRef}
        role='button'
        tabIndex={0}
        onMouseEnter={openMessage}
        onMouseLeave={closeMessage}
        onFocus={openMessage}
        onBlur={closeMessage}
        style={badgeStyle}
      >
        {badgeContent}
      </span>
      {showPopover && (
        <Overlay
          target={targetRef.current}
          show={showMessage}
          placement={messagePlacement}
          popperConfig={{
            strategy: 'fixed',
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, 8],
                },
              },
              {
                name: 'flip',
                options: {
                  fallbackPlacements: ['left', 'top', 'bottom'],
                  padding: 12,
                },
              },
              {
                name: 'preventOverflow',
                options: {
                  boundary: 'viewport',
                  padding: 12,
                },
              },
            ],
          }}
        >
          {renderPopover()}
        </Overlay>
      )}
    </>
  )
}

export default SyncMessageBadge
